import { Product, ProductPrice } from "@/api/product-api";
import { LanCode } from "@/store/reducers/setting";
import { LanguageCodeToLanCode } from "@/utils/LocaleUtil";
import { PageSeoData } from "@/types/seo";
import { SEO_CONSTANTS, buildBreadcrumb } from "../constants";
import { productSeoData } from "../product-seo-data";

/**
 * 상품 이미지 배열 생성 (중복 제거)
 */
function getProductImages(product: Product): string[] {
  const images: string[] = [];
  const seen = new Set<string>();

  const addImage = (url: string | undefined | null) => {
    if (url && !seen.has(url)) {
      seen.add(url);
      // 상대 경로인 경우 절대 경로로 변환
      const absoluteUrl = url.startsWith("http")
        ? url
        : `${SEO_CONSTANTS.BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
      images.push(absoluteUrl);
    }
  };

  // 우선순위: bannerImage > thumbnail > productImages
  addImage(product.bannerImage?.desktopSrc);
  addImage(product.thumbnail);
  product.productImages?.forEach(addImage);

  // 이미지가 없으면 기본 이미지 사용
  if (images.length === 0) {
    images.push(`${SEO_CONSTANTS.BASE_URL}${SEO_CONSTANTS.DEFAULT_OG_IMAGE}`);
  }

  return images;
}

/**
 * 상품 페이지 SEO 생성기
 */
export function generateProductSeo(
  product: Product,
  locale: LanCode,
  /**
   * 화면이 그리는 제품과 그 국가의 가격 — `resolveSeoOffer`(`libs/seo/product-offer.ts`). 주면
   * 제목·구조화 데이터가 그 제품(`?bundle=` 번들 · camo 변형)이 되고 가격은 실판매가가 된다(#5858).
   * 안 주면 가격(offers)을 싣지 않는다 — 국가를 모르는 채 USD 를 박으면 다른 국가 페이지와 어긋난다.
   * 브레드크럼·canonical 은 늘 기본 제품(`product`)이다.
   */
  offer?: {
    displayProduct: Product;
    price: ProductPrice | null;
    /** 이 페이지의 실제 주소(쿼리 포함) — Offer.url */
    url: string;
  },
): PageSeoData {
  const shown = offer?.displayProduct ?? product;
  const price = offer?.price ?? null;

  const totalStock = shown.skuOptions?.reduce(
    (acc, opt) => acc + (opt.stock ?? 0),
    0,
  );
  const availability = totalStock > 0 ? "InStock" : "OutOfStock";

  // 이미지 배열 생성 (중복 제거됨)
  const images = getProductImages(shown);

  // 비영어는 제품별 현지화 문구(#6104) — 없으면(영어 · 등록 안 된 제품) 종전대로 어드민 값.
  // `shown` 기준이라 `?color=camo` 처럼 화면이 변형 제품을 그리면 그 제품 문구가 나간다.
  //
  // ⚠️ 상품 페이지는 Next locale(`ko` · `ja` · `zh`)을 `as LanCode` 로 넘긴다(`buy-tactsuit/[slug].tsx`) —
  // `LanCode` 값은 `kr` · `jp` · `cn` 이라 그대로 찾으면 한국어 · 일본어 · 중국어가 조용히 영어로 떨어진다.
  // 브레드크럼은 받은 값 그대로 써야 해서 호출부를 바꾸지 않고 여기서만 맞춘다.
  const lan =
    (LanguageCodeToLanCode as Record<string, LanCode>)[locale] ?? locale;
  const localized =
    lan === LanCode.English ? undefined : productSeoData[shown.slug]?.[lan];

  return {
    title: localized?.title ?? `${shown.name} | bHaptics`,
    description:
      localized?.description ||
      shown.shortDesc ||
      shown.desc?.slice(0, 160) ||
      `Buy ${shown.name} - Next generation haptic technology by bHaptics`,
    ogImage:
      shown.bannerImage?.desktopSrc ||
      shown.thumbnail ||
      SEO_CONSTANTS.DEFAULT_OG_IMAGE,
    ogType: "product",
    canonicalPath: `/shop/buy-tactsuit/${product.slug}`,
    keywords: [product.name, "haptic", "bHaptics", "VR", ...product.tags],
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Product",
        name: shown.name,
        // 메타 설명과 같은 언어로 — 비영어 페이지의 구조화 데이터만 영어로 남지 않게
        description:
          localized?.description || shown.shortDesc || shown.desc || null,
        // 단일 이미지 대신 배열로 제공 (Google 권장)
        image: images.length === 1 ? images[0] : images,
        brand: {
          "@type": "Brand",
          name: "bHaptics",
        },
        // SKU 식별자 추가 (Google 권장) - 대표 SKU(첫 옵션)
        ...(shown.skuOptions?.[0]?.sku ? { sku: shown.skuOptions[0].sku } : {}),
        // MPN (Manufacturer Part Number) - slug 기반
        mpn: `BHAPTICS-${shown.slug.toUpperCase()}`,
        // ★가격은 **실판매가**(정가 − 할인)다 — 화면의 굵은 가격. 종전은 기준가 USD 정가였다(#5858).
        ...(price && offer
          ? {
              offers: {
                "@type": "Offer",
                price: price.originalAmount - price.discountAmount,
                priceCurrency: price.currency,
                availability: `https://schema.org/${availability}`,
                url: offer.url,
                seller: {
                  "@type": "Organization",
                  name: "bHaptics",
                },
              },
            }
          : {}),
      },
      buildBreadcrumb(locale, [
        { name: "Home", path: "" },
        // 「Shop」은 **제품 목록**을 가리킨다 (#5701). 종전 `/shop/buy-tactsuit` 는
        // 도달 경로가 0인 인덱스라 크롤러만 따라오는 자리였고, 지금은 `/tactsuit/` 로
        // 리다이렉트된다 — 브레드크럼이 홉을 하나 더 타지 않도록 목적지를 직접 쓴다.
        { name: "Shop", path: "/tactsuit" },
        { name: product.name, path: `/shop/buy-tactsuit/${product.slug}` },
      ]),
    ],
  };
}
